import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { MonthTarget, TargetComponentValue } from "@/types/user/target";
import { DateTime } from "luxon";

import { allowanceLabel, format_with_unit } from "./utils";

const BONUS_GRADIENTS = [
    "bg-gradient-to-br from-blue-500 to-blue-600",
    "bg-gradient-to-br from-blue-500 to-indigo-600",
    "bg-gradient-to-br from-indigo-500 to-indigo-600",
    "bg-gradient-to-br from-indigo-500 to-purple-600",
    "bg-gradient-to-br from-purple-500 to-purple-600",
    "bg-gradient-to-br from-purple-500 to-pink-600",
    "bg-gradient-to-br from-pink-500 to-pink-600",
    "bg-gradient-to-br from-pink-500 to-red-600",
    "bg-gradient-to-br from-red-500 to-red-600",
    "bg-gradient-to-br from-red-500 to-orange-600",
    "bg-gradient-to-br from-orange-500 to-orange-600",
    "bg-gradient-to-br from-orange-500 to-yellow-600",
    "bg-gradient-to-br from-yellow-500 to-yellow-600",
    "bg-gradient-to-br from-yellow-500 to-green-600",
    "bg-gradient-to-br from-green-500 to-green-600",
    "bg-gradient-to-br from-green-500 to-teal-600",
    "bg-gradient-to-br from-teal-500 to-teal-600",
    "bg-gradient-to-br from-teal-500 to-cyan-600",
    "bg-gradient-to-br from-cyan-500 to-cyan-600",
    "bg-gradient-to-br from-cyan-500 to-blue-600"
];

const PROGRESS_BAR_GRADIENTS = [
    "[&>div]:bg-gradient-to-br [&>div]:from-blue-500 [&>div]:to-blue-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-blue-500 [&>div]:to-indigo-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-indigo-500 [&>div]:to-indigo-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-indigo-500 [&>div]:to-purple-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-purple-500 [&>div]:to-purple-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-purple-500 [&>div]:to-pink-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-pink-500 [&>div]:to-pink-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-pink-500 [&>div]:to-red-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-red-500 [&>div]:to-red-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-red-500 [&>div]:to-orange-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-orange-500 [&>div]:to-orange-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-orange-500 [&>div]:to-yellow-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-yellow-500 [&>div]:to-yellow-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-yellow-500 [&>div]:to-green-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-green-500 [&>div]:to-green-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-green-500 [&>div]:to-teal-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-teal-500 [&>div]:to-teal-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-teal-500 [&>div]:to-cyan-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-cyan-500 [&>div]:to-cyan-600",
    "[&>div]:bg-gradient-to-br [&>div]:from-cyan-500 [&>div]:to-blue-600"
];

interface TargetDetailToDateProps {
    monthTarget: MonthTarget;
    components: TargetComponentValue[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function TargetMonthToDateDetail({ monthTarget, components: allComponentValues, open, onOpenChange }: TargetDetailToDateProps) {
    // Group components by their class and sum all values
    const groupedComponents =
        monthTarget.components
            .filter(component => component.component_class)
            .map(component => {
                const componentValues = allComponentValues.filter(c => c.target_component === component.id);
                const weight = monthTarget.system_class?.weights.find(w => w.name === component.component_class?.name)?.weight ?? 0;

                const totalValues = {
                    current: component.total_to_date_current ?? 0,
                    goal: component.total_to_date_goal ?? 1
                };

                return {
                    component: component,
                    weight,
                    totalValues,
                    values: componentValues
                };
            }) ?? [];

    // Prepare payplan data for the histogram
    const progress = (monthTarget.to_date_current / (monthTarget.to_date_goal || 1)) * 100;
    const payplanSteps = monthTarget.system_class?.payplan.steps.sort((a, b) => a.min - b.min);

    const getGradientForBonus = (bonus: number, forProgress: boolean = false) => {
        if (bonus === 0) return forProgress ? "[&>div]:bg-black" : "bg-black";
        const gradientIndex = Math.min(Math.floor(bonus / 100), BONUS_GRADIENTS.length - 1);
        return forProgress ? PROGRESS_BAR_GRADIENTS[gradientIndex] : BONUS_GRADIENTS[gradientIndex];
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl">
                <DialogHeader>
                    <DialogTitle>Quelle est ma performance depuis le début du mois ?</DialogTitle>
                </DialogHeader>
                <div className="grid gap-5 py-4">
                    {/* <div className="space-y-2">
                        <h4 className="font-medium">Mes points depuis le début du mois</h4>
                        <div className="flex items-center gap-2">
                            <Progress value={progress} className="flex-1 [&>div]:bg-[#3b82f6]" />
                            <span className="text-sm text-muted-foreground">
                                {monthTarget.to_date_current} / {Math.ceil(monthTarget.to_date_goal)} points
                            </span>
                        </div>
                    </div> */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-muted-foreground text-red-700">Les montants indiqués sont donnés à titre indicatif et sous soumis à des ajustements</span>
                        </div>
                        {payplanSteps && (
                            <div className="grid grid-cols-3 gap-4">
                                {payplanSteps.map((step, index) => {
                                    if (progress >= step.min && progress < step.max) {
                                        const currentStep = step;
                                        const currentStepMinPoints = currentStep.min;
                                        const nextSteps = payplanSteps.slice(index + 1, index + 3);
                                        return (
                                            <>
                                                <div key="current" className={`flex flex-col justify-between space-y-2 rounded-lg p-4 ${getGradientForBonus(currentStep.bonus)}`}>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-white">
                                                            Bonus estimé si performance stable jusqu'au{" "}
                                                            {DateTime.fromISO(monthTarget.month).plus({ months: 1 }).minus({ days: 1 }).toFormat("dd MMMM", { locale: "fr" })}
                                                        </span>
                                                        <span className="min-w-[60px] text-sm font-semibold text-white">{monthTarget.to_date_payplan_bonus}€ bruts</span>
                                                    </div>
                                                    <div className="">
                                                        <Progress value={100} className="h-2 [&>div]:bg-white/50" />
                                                        <span className="text-xs text-white/80">{monthTarget.to_date_current} points</span>
                                                    </div>
                                                </div>
                                                {nextSteps.map((nextStep, nextIndex) => {
                                                    const pointsNeeded = Math.ceil(nextStep.min - monthTarget.to_date_current);
                                                    return (
                                                        <div key={nextStep.min} className={`rounded-[10px] p-[5px] ${getGradientForBonus(nextStep.bonus)}`}>
                                                            <div className="flex h-full flex-col justify-between space-y-2 rounded-[5px] bg-white p-3">
                                                                <div className="flex items-center justify-between text-black">
                                                                    <span className="text-sm font-medium">
                                                                        Palier {index + nextIndex + 1} • {Math.round(nextStep.min)} points
                                                                    </span>
                                                                    <span className="text-sm font-semibold ">{nextStep.bonus}€ bruts</span>
                                                                </div>
                                                                <div className="">
                                                                    <Progress
                                                                        value={((monthTarget.to_date_current - currentStepMinPoints) / (nextStep.min - currentStepMinPoints)) * 100}
                                                                        className={`h-2 ${getGradientForBonus(nextStep.bonus, true)}`}
                                                                    />
                                                                    <span className="text-xs text-black/80">{pointsNeeded} points manquants</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        )}
                    </div>
                    <div className="mt-4 space-y-4">
                        <h4 className="font-medium">Décomposition de l'objectif</h4>
                        <div className="grid gap-x-16 gap-y-10 lg:grid-cols-2">
                            {groupedComponents.map(({ component, weight, totalValues }) => {
                                if (!component.component_class) return null;
                                if (weight === 0) return null;
                                const points = component.composite_to_date_value ?? 0;
                                const weightedPoints = points * weight;
                                const displayCurrent = component.total_to_date_current ?? 0;
                                const displayGoal = component.total_to_date_goal ?? 0;

                                return (
                                    <div key={component.component_class?.name} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium">
                                                    {component.component_class?.verbose_name} : {weightedPoints.toFixed(0)} points
                                                </p>
                                            </div>
                                        </div>
                                        <div className="space-y-2 pr-4">
                                            <div className="flex items-center justify-between">
                                                {component.component_class?.is_penalty ? (
                                                    <span className="text-xs text-muted-foreground">{allowanceLabel(displayCurrent, displayGoal)}</span>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">
                                                        {format_with_unit(displayCurrent, component.component_class.target_unit)} sur{" "}
                                                        {format_with_unit(displayGoal, component.component_class.target_unit)}
                                                    </span>
                                                )}
                                            </div>
                                            <Progress value={(totalValues.current / totalValues.goal) * 100} className="h-2 [&>div]:bg-[#3b82f6]" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
